import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError } from '../../lib/api-client';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { PatientUhidAutocomplete } from '../../components/ui/PatientUhidAutocomplete';
import { FieldError } from '../../components/ui/FieldError';
import { usePatientUhidSearch } from '../../lib/patient-uhid-search';
import { normalizePhoneInput } from '../../lib/phone-input';
import { useToast } from '../../lib/toast';
import { useIpdDaycareAdmission } from '../ipd-daycare/ipd-daycare.queries';
import { useCreateOtCase, useOtLookup } from './ot.queries';
import { FieldLabel, LookupSelect, PatientDemographicFields, PatientIdentityFields, ResponsiblePersonFields } from './ot.shared';
import { blankPatientFields, blankResponsiblePerson, patientFieldsFromOption, todayInput } from './ot.utils';
import type { CreateOtCasePayload, PatientFormFields } from './types';

interface RegistrationInfoState {
  underDoctor: string | null;
  bedCategory: string | null;
  bed: string | null;
  referredBy: string | null;
}

const blankRegistrationInfo = (): RegistrationInfoState => ({
  underDoctor: null, bedCategory: null, bed: null, referredBy: null,
});

// OT patients go to the OT ward by default: an exact "OT" ward name wins, otherwise a name containing the word OT.
const findOtWardId = (wards: { id: string; label: string }[] | undefined) => {
  const list = wards ?? [];
  return (list.find((ward) => ward.label.trim().toUpperCase() === 'OT') ?? list.find((ward) => /\bOT\b/i.test(ward.label)))?.id ?? null;
};

const validateOtRegistrationFields = (
  registrationDate: string,
  insuranceType: string,
  patient: PatientFormFields,
  info: RegistrationInfoState,
  responsiblePerson: ReturnType<typeof blankResponsiblePerson>,
) => {
  const errors: Record<string, string> = {};
  if (!registrationDate) errors.registrationDate = 'Scheduled Date is required.';
  if (!insuranceType) errors.insuranceType = 'Patient Type is required.';
  if (!patient.name.trim()) errors.name = "Patient's name is required.";
  if ((patient.phone ?? '').trim() && !/^\d{10}$/.test((patient.phone ?? '').trim())) errors.phone = 'Enter a 10 digit phone number.';
  if ((patient.guardianContactNo ?? '').trim() && !/^\d{10}$/.test((patient.guardianContactNo ?? '').trim())) errors.guardianContactNo = 'Enter a 10 digit phone number.';
  if (!(patient.guardianName ?? '').trim()) errors.guardianName = 'Guardian Name is required.';
  if (!patient.relation) errors.relation = 'Relation is required.';
  if (!(patient.address ?? '').trim()) errors.address = 'Address is required.';
  if (!(patient.state ?? '').trim()) errors.state = 'State is required.';
  if (!info.underDoctor) errors.underDoctor = 'Under Doctor is required.';
  if (!info.bedCategory) errors.bedCategory = 'Bed Category is required.';
  if (!info.bed) errors.bed = 'Bed is required.';
  if (!info.referredBy) errors.referredBy = 'Referred By is required.';
  if (responsiblePerson.phone.trim() && !/^\d{10}$/.test(responsiblePerson.phone.trim())) errors.responsiblePersonPhone = 'Enter a 10 digit phone number.';
  return errors;
};

export const OtRegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const mutation = useCreateOtCase();
  const sourceAdmissionId = searchParams.get('from-admission');
  const sourceQuery = useIpdDaycareAdmission(sourceAdmissionId ?? undefined);

  const [registrationDate, setRegistrationDate] = useState(todayInput);
  const [insuranceType, setInsuranceType] = useState('');
  const [insuranceNumber, setInsuranceNumber] = useState('');
  const [diagnosis, setDiagnosis] = useState('');

  const [patientSearch, setPatientSearch] = useState('');
  const debouncedPatientSearch = usePatientUhidSearch(patientSearch);
  const patients = useOtLookup('patients', debouncedPatientSearch);
  const [patient, setPatient] = useState<PatientFormFields>(blankPatientFields);
  const states = useOtLookup('states');
  const districts = useOtLookup('districts', '', { state: patient.state ?? '' });

  const [info, setInfo] = useState<RegistrationInfoState>(blankRegistrationInfo);
  const set = <K extends keyof RegistrationInfoState>(key: K, value: RegistrationInfoState[K]) => setInfo((current) => ({ ...current, [key]: value }));
  const doctors = useOtLookup('doctors');
  const wards = useOtLookup('wards');
  const beds = useOtLookup('beds', '', { ward: info.bedCategory ?? '' });
  const referrals = useOtLookup('referrals');
  const tpas = useOtLookup('tpas');

  const [responsiblePerson, setResponsiblePerson] = useState(blankResponsiblePerson);
  const [showErrors, setShowErrors] = useState(false);
  const errors = showErrors ? validateOtRegistrationFields(registrationDate, insuranceType, patient, info, responsiblePerson) : {};

  const prefillNotified = useRef(false);
  useEffect(() => {
    const row = sourceQuery.data;
    if (!row) return;
    setPatientSearch(row.patientSnapshot.uhid ?? '');
    setPatient({
      patientId: row.patient.id,
      uhid: row.patientSnapshot.uhid,
      name: row.patientSnapshot.name,
      phone: row.patientSnapshot.phone ? normalizePhoneInput(row.patientSnapshot.phone) : null,
      gender: row.patientSnapshot.gender,
      maritalStatus: row.patientSnapshot.maritalStatus,
      guardianName: row.patientSnapshot.guardianName,
      relation: row.patientSnapshot.relation,
      guardianContactNo: null,
      dateOfBirth: row.patientSnapshot.dateOfBirth,
      ageYears: row.patientSnapshot.ageYears,
      ageMonths: row.patientSnapshot.ageMonths,
      ageDays: row.patientSnapshot.ageDays,
      address: row.patientSnapshot.address,
      state: row.patientSnapshot.state ?? 'West Bengal (WB)',
      district: row.patientSnapshot.district,
      pinCode: row.patientSnapshot.pinCode,
      aadhaar: row.patientSnapshot.aadhaar,
    });
    setInsuranceType(row.insuranceType ?? '');
    setInsuranceNumber(row.insuranceNumber ?? '');
    setDiagnosis(row.diagnosisAtAdmission ?? '');
    setResponsiblePerson({
      name: row.responsiblePerson.name ?? '',
      relation: row.responsiblePerson.relation ?? '',
      phone: row.responsiblePerson.phone ? normalizePhoneInput(row.responsiblePerson.phone) : '',
      age: row.responsiblePerson.age,
      address: row.responsiblePerson.address ?? '',
    });
    if (!prefillNotified.current) {
      prefillNotified.current = true;
      showToast(`Patient details imported from IPD/Daycare admission ${row.ipdNumber}.`, 'success');
    }
  }, [sourceQuery.data, showToast]);
  useEffect(() => {
    const row = sourceQuery.data;
    if (!row) return;
    setInfo((current) => ({
      ...current,
      underDoctor: current.underDoctor ?? doctors.data?.find((item) => item.id === row.underDoctor?.id)?.id ?? null,
      referredBy: current.referredBy ?? referrals.data?.find((item) => item.id === row.referredBy?.id)?.id ?? null,
    }));
  }, [sourceQuery.data, doctors.data, referrals.data]);
  useEffect(() => {
    const otWardId = findOtWardId(wards.data);
    if (otWardId) setInfo((current) => (current.bedCategory ? current : { ...current, bedCategory: otWardId }));
  }, [wards.data]);
  useEffect(() => {
    if (sourceQuery.isError && !prefillNotified.current) {
      prefillNotified.current = true;
      showToast('The IPD/Daycare admission details could not be loaded for prefill.', 'warning');
    }
  }, [sourceQuery.isError, showToast]);

  const choosePatient = (id: string) => {
    const option = patients.data?.find((item) => item.id === id);
    if (!option) { setPatient(blankPatientFields()); return; }
    setPatient(patientFieldsFromOption(option));
  };
  const changePatientSearch = (value: string) => { setPatientSearch(value); if (patient.patientId) setPatient(blankPatientFields()); };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    if (Object.keys(validateOtRegistrationFields(registrationDate, insuranceType, patient, info, responsiblePerson)).length > 0) return;
    const payload: CreateOtCasePayload = {
      ...patient,
      otType: sourceQuery.data ? 'MOVED BY IPD' : 'DIRECT',
      registrationDate,
      insuranceType,
      insuranceNumber: insuranceNumber || null,
      underDoctor: info.underDoctor,
      bedCategory: info.bedCategory,
      bed: info.bed,
      referredBy: info.referredBy,
      responsiblePerson,
      diagnosis: diagnosis.trim() || null,
    };
    try {
      const created = await mutation.mutateAsync(payload);
      showToast('OT admission saved successfully.', 'success');
      void navigate('/ot');
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'OT admission could not be saved.', 'error');
    }
  };

  const busy = mutation.isPending;
  return (
    <div className="page-stack ipd-daycare-page">
      <div className="page-heading"><div><p className="eyebrow">OT</p><h1>OT Admission</h1><p>Patient details, admission information, and responsible person.</p></div><Link className="button button-secondary" to="/ot">Back to OT List</Link></div>
      <form className="content-card ipd-daycare-registration-card" noValidate onSubmit={(event) => void submit(event)}>
        <section className="ipd-daycare-section">
          <div className="dialysis-top-row">
            <label><FieldLabel required>Scheduled Date</FieldLabel><input type="date" value={registrationDate} onChange={(event) => setRegistrationDate(event.target.value)} required aria-invalid={Boolean(errors.registrationDate)} /><FieldError message={errors.registrationDate} /></label>
            <label><FieldLabel required>Patient Type</FieldLabel><select value={insuranceType} onChange={(event) => setInsuranceType(event.target.value)} required aria-invalid={Boolean(errors.insuranceType)}><option value="">Select</option>{(tpas.data ?? []).map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}</select><FieldError message={errors.insuranceType} /></label>
            <label><FieldLabel>Insurance No</FieldLabel><input value={insuranceNumber} onChange={(event) => setInsuranceNumber(event.target.value)} maxLength={100} /></label>
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Patient Details</h2>
          <div className="ipd-daycare-patient-row1">
            <label><FieldLabel>UHID</FieldLabel><PatientUhidAutocomplete id="ot-patient" value={patientSearch} selectedId={patient.patientId} options={patients.data} isFetching={patients.isFetching} isError={patients.isError} onInputChange={changePatientSearch} onChange={choosePatient} /></label>
            <PatientIdentityFields value={patient} onChange={setPatient} errors={errors} />
          </div>
          <div className="ipd-daycare-patient-row2">
            <PatientDemographicFields value={patient} onChange={setPatient} states={states.data} districts={districts.data} errors={errors} />
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Patient Admission Information</h2>
          <div className="ipd-daycare-admission-row ot-admission-row">
            <label><FieldLabel required>Under Doctor</FieldLabel><LookupSelect id="ot-under-doctor" value={info.underDoctor ?? ''} options={doctors.data} placeholder="Select Doctor" ariaInvalid={Boolean(errors.underDoctor)} onChange={(underDoctor) => set('underDoctor', underDoctor || null)} /><FieldError message={errors.underDoctor} /></label>
            <label><FieldLabel>Department</FieldLabel><input value={doctors.data?.find((doctor) => doctor.id === info.underDoctor)?.department ?? ''} placeholder="Select Under Doctor" readOnly /></label>
            <label><FieldLabel required>Bed Category</FieldLabel><input value={wards.data?.find((ward) => ward.id === info.bedCategory)?.label ?? 'OT'} readOnly aria-invalid={Boolean(errors.bedCategory)} /><FieldError message={errors.bedCategory} /></label>
            <label><FieldLabel required>Bed</FieldLabel><LookupSelect id="ot-bed" value={info.bed ?? ''} options={beds.data} placeholder={info.bedCategory ? 'Select Bed' : 'Select Bed Category first'} disabled={!info.bedCategory} ariaInvalid={Boolean(errors.bed)} onChange={(bed) => set('bed', bed || null)} /><FieldError message={errors.bed} /></label>
            <label><FieldLabel required>Referred By</FieldLabel><LookupSelect id="ot-referred-by" value={info.referredBy ?? ''} options={referrals.data} placeholder="Select Referral" ariaInvalid={Boolean(errors.referredBy)} onChange={(referredBy) => set('referredBy', referredBy || null)} /><FieldError message={errors.referredBy} /></label>
            <label className="ipd-daycare-wide"><CkEditorField label="Diagnosis" value={diagnosis} onChange={setDiagnosis} /></label>
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Responsible Person Details</h2>
          <div className="ipd-daycare-responsible-row">
            <ResponsiblePersonFields value={responsiblePerson} onChange={setResponsiblePerson} errors={errors} />
          </div>
        </section>
        <div className="ipd-daycare-form-actions"><button className="button button-primary" type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save'}</button></div>
      </form>
    </div>
  );
};
